import React, {useState, useEffect, useRef} from 'react';
import {
    CircularProgress,
    Paper,
    Typography
} from "@material-ui/core";
import {Alert} from "@material-ui/lab";
import useStyles from "../styles/styles";
import {useQuery, useMutation} from "@apollo/react-hooks";
import {gql} from "apollo-boost";
import ConfigTable from "../components/Config/ConfigTable";
import ConfigListModal from "../components/Config/ConfigListModal";
import ConfigScheduleModal from "../components/Config/ConfigScheduleModal";
import ConfigCapacityModal from '../components/Config/ConfigCapacityModal';
import ConfigStateModal from "../components/Config/ConfigStateModal";

const Configs = () => {
    const classes = useStyles();
    const [success, setSuccess] = useState(false);
    const [modalData, setModalData] = useState({
        modalType: '',
        category: false,
        data: false,
        isPair: false,
        value: []
    });
    const [finalValue, setFinalValue] = useState('');
    const closeModal = () => {
        setModalData({
            modalType: '',
            category: false,
            data: false,
            isPair: false,
            value: []
        });
    };
    /**
     * Config Data GraphQL
     */
    const {loading, error, data, refetch} = useQuery(gql`
        query allConfigs {
            allConfig {
                nodes {
                    id
                    category
                    dateCreated
                    dateModified
                    key
                    type
                    valueBool
                    valueDecimal
                    valueInt
                    valueIntArray
                    valueString
                    valueStringArray
                    valuePairStringStringArray {
                        key
                        value
                    }
                    valueCampaignSchedule {
                        available {
                            dayOfWeek
                            ranges {
                                opening
                                closing
                            }
                        }
                        federalObservances
                        name
                        timeZone
                    }
                    valueCampaignCapacity {
                        id
                        name
                        limit
                        resetFrequency
                        constraints {
                            field
                            values
                        }
                    }
                }
            }
            allTimeZones: __type(name: "TimeZone") {
                enumValues {
                    name
                }
            }
            allObservances: __type(name: "Observance") {
                enumValues {
                    name
                }
            }
            allDays: __type(name: "DayOfWeek") {
                enumValues {
                    name
                }
            }
            allObservance: __type(name: "Observance") {
                enumValues {
                    name
                }
            }
            allConfigCategory: __type(name: "ConfigCategory"){
                enumValues {
                    name
                }
            }
            allConfigType: __type(name: "ConfigType"){
                enumValues {
                    name
                }
            }
            allAffiliate {
                nodes {
                    id
                    name
                    key
                }
            }
            allTrafficSource(filter: {
                includeDeleted: true
            }) {
                nodes {
                    id
                    name
                    legacyPromoId
                    minimumPrice
                    affiliate {
                        id
                        name
                    }
                    isDeleted
                }
            }        
        }
    `, {
        notifyOnNetworkStatusChange: true,
        context: {
            clientName: "kgraphql"
        }
    });

    /**
     * Create Config Graphql
     */
    const [createConfig, {loading: createConfigLoading, error: createConfigError}] = useMutation(gql`
        mutation CreateConfig($input: String!) {
            createConfig(input: $input) {
                id
                category
                dateCreated
                dateModified
                key
                type
                valueBool
                valueInt
                valueDecimal
                valueIntArray
                valueString
                valueStringArray
                valuePairStringStringArray {
                    key
                    value
                }
                valueCampaignSchedule {
                    available {
                        dayOfWeek
                        ranges {
                            opening
                            closing
                        }
                    }
                    federalObservances
                    name
                    timeZone
                }
                valueCampaignCapacity {
                    id
                    name
                    limit
                    usage
                    resetFrequency
                }
            }
        }
    `, {
        context: {
            clientName: "kgraphql"
        }
    });

    /**
     * Update Config Graphql
     */
    const [updateConfig, {loading: updateConfigLoading, error: updateConfigError}] = useMutation(gql`
        mutation UpdateConfig($input: String!) {
            updateConfig(input: $input) {
                id
                category
                dateCreated
                dateModified
                key
                type
                valueBool
                valueInt
                valueDecimal
                valueIntArray
                valueString
                valueStringArray
                valuePairStringStringArray {
                    key
                    value
                }
                valueCampaignSchedule {
                    available {
                        dayOfWeek
                        ranges {
                            opening
                            closing
                        }
                    }
                    federalObservances
                    name
                    timeZone
                }
                valueCampaignCapacity {
                    id
                    name
                    limit
                    usage
                    resetFrequency
                }
            }
        }
    `, {
        context: {
            clientName: "kgraphql"
        }
    });
    /**
     * Delete Config GraphQL
     */
    const [deleteConfig, {loading: deleteConfigLoading}] = useMutation(gql`
        mutation DeleteConfig(
            $id: UUID!
        ) {
            deleteConfig(id: $id)
        }
    `, {
        context: {
            clientName: "kgraphql"
        }
    });
    /**
     * Sync to Prod
     */
    const [updateConfigFromTest, {loading: syncLoading, data: syncDataResp}] = useMutation(gql`
        mutation syncData {
            updateConfigFromTest
        }
    `, {
        context: {
            clientName: "kgraphql"
        }
    });

    useEffect(() => {
        if (!deleteConfigLoading) {
            setFinalValue('');
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteConfigLoading]);

    useEffect(() => {
        if (!createConfigLoading) {
            closeModal();
            setFinalValue('');
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createConfigLoading]);

    useEffect(() => {
        if (!updateConfigLoading) {
            closeModal();
            setFinalValue('');
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateConfigLoading]);

    useEffect(() => {
        if (syncDataResp && syncDataResp.updateConfigFromTest) {
            setTimeout(() => setSuccess(false), 3000);
            setFinalValue('');
            refetch();
        }
    }, [syncDataResp]);

    /**
     * Main Return for Component
     */
    return (
        <>
            <Typography variant="h2">
                Configurations
            </Typography>
            {!loading && (error || updateConfigError || createConfigError) ? (
                <Paper className={classes.paper}>
                    <Alert severity="error">There is an error!</Alert>
                    <pre>{JSON.stringify(error || updateConfigError || createConfigError, null, 2)}</pre>
                </Paper>
            ) : null}
            {loading || syncLoading ? (
                <Paper className={classes.paper}>
                    <div className={classes.loadingBar}>
                        <CircularProgress
                            size={50}
                            thickness={4}
                        />
                    </div>
                </Paper>
            ) : null}
            {!loading && data && data.allConfig.nodes && !syncLoading ? (
                <>
                    {success ? (
                        <Alert severity="success">Configurations are synced!</Alert>
                    ) : null}
                    <ConfigTable
                        refetch={refetch}
                        data={data}
                        setSuccess={setSuccess}
                        updateConfigFromTest={updateConfigFromTest}
                        updateConfigLoading={updateConfigLoading}
                        createConfig={createConfig}
                        deleteConfig={deleteConfig}
                        updateConfig={updateConfig}
                        setModalData={setModalData}
                        finalValue={finalValue}
                        setFinalValue={setFinalValue}
                    />
                </>
            ) : null}
            {modalData.modalType === 'configList' ? (
                <ConfigListModal
                    data={data}
                    modalData={modalData}
                    closeModal={closeModal}
                />
            ) : null}
            {modalData.modalType === 'configSchedule' ? (
                <ConfigScheduleModal
                    data={data}
                    modalData={modalData}
                    closeModal={closeModal}
                />
            ) : null}
            {modalData.modalType === 'configCapacity' ? (
                <ConfigCapacityModal
                    data={data}
                    modalData={modalData}
                    closeModal={closeModal}
                />
            ) : null}
            {modalData.modalType === 'configState' ? (
                <ConfigStateModal
                    data={data}
                    modalData={modalData}
                    closeModal={closeModal}
                />
            ) : null}
        </>
    );
};

export default Configs;